// External libraries
import _ from 'lodash';
import React, { Component, type PropsWithChildren, type ReactNode, type RefObject } from 'react';
import classNames from 'classnames';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Styles
import './index.scss';

// UI components

// Common utilities and helpers
import { getPublishedViewUid } from 'common/views/publicationHelpers';
import getRevisionSeq from 'common/js_utils/getRevisionSeq';
import { isStory, isStoryDraft } from 'common/views/view_types';
import { assetIsDraft } from 'common/views/helpers';
import { assetStatusFor } from 'common/views/asset_status';
import { isMobileOrTablet } from 'common/visualizations/helpers/MediaQueryHelper';
import { currentUserCanMakeAssets } from 'common/current_user';

// Core services and APIs
import { assetIdFor, fetchApprovalsGuidanceV2 } from 'common/core/approvals';
import airbrake from 'common/airbrake';

// Feature flags and i18n
import { FeatureFlags } from '@socrata/platform/feature-flags';
import { I18n } from 'common/i18n';
import { isTablePrintingImprovementForScheduledSendEnabled } from 'common/visualizations/helpers/VifSelectors';

// AssetActionBar component-specific utilities
import { anyActionAvailable } from 'common/components/AssetActionBar/lib/any_action_available';
import { getAllowedSecondaryActions } from 'common/components/AssetActionBar/lib/secondary_actions';
import { getPrimaryAction } from 'common/components/AssetActionBar/lib/primary_action';

// Local components
import FederationTargets from './components/federation_targets';
import { PublicationAction, type RenderPrimaryButtonFunction } from './components/publication_action';
import PublicationState from './components/publication_state';
import ResetToDefaultButton from './components/reset_to_default_button';
import SaveViewButton from './components/save_view_button';
import ViewOnSourceDomainButton from './components/view_on_source_domain_button';

// Types
import { type View, type ViewPermissions } from 'common/types/view';
import { type GuidanceSummaryV2 } from 'common/types/approvals';
import type InteractiveUser from 'common/types/users/interactiveUser';
import { type FilterMetadataCollection } from 'common/types/filterMetadata';

const queryClient = new QueryClient();

const scope = 'shared.components.asset_action_bar';

export interface AssetActionBarProps {
  user: InteractiveUser;
  view: View;
  publishedViewUid?: string;
  editDraftButton?: ReactNode;

  /** Optional, called if user selects "Copy" from the more actions menu. */
  onCopy?: (name: string) => void;
  /** Optional, shows the 'Based On' caption on the copy modal */
  copyBasedOn?: string;

  onPublish?: () => (permissions?: ViewPermissions) => void;

  renderPrimaryButton?: RenderPrimaryButtonFunction;

  /** Optional, children to place at the left of the bar (i.e., a menu drawer button). */
  leftChildren?: ReactNode;

  /**
   * Optional, will fetch internally if this is not provided. Some contexts using A2B
   * already have this information available - we can avoid making a duplicate request
   * this way.
   */
  approvalsGuidance?: GuidanceSummaryV2;
  showDSLPButton?: boolean;

  /**
   * Called once if the bar has decided it isn't going to show anything. This is
   * sometimes helpful when the surrounding application needs to show legacy
   * components if the asset action bar decides to hide. For anything beyond
   * simple usages, it is recommended to call anyActionAvailable directly to see
   * if the A2B will decide to render.
   *
   * Current uses of this callback don't have direct access to approvalsGuidance
   * and thus rely on A2B to fetch it itself, so calling anyActionAvailable is
   * not trivial.
   */
  willNotShowBarBecauseUserCantDoAnything?: () => void;

  /**
   * Called once if the bar has decided it will show up. Used for same scenarios
   * as willNotShowBarBecauseUserCantDoAnything.
   */
  willShowBar?: () => void;

  customSaveFunc?: () => void;

  inVQE?: boolean;

  // If the story has been personalized, we put up a reset to default button.
  personalizedStory?: boolean;
  titleOverride?: ReactNode;

  /**
   * render the dataset title as an `<h1>` instead of the default `<span>`.
   * opt-in so consumers whose surrounding page already provides a top-level
   * heading do not end up with duplicate `<h1>` elements.
   */
  titleAsHeading?: boolean;

  /**
   * keep the AssetActionBar mounted as a title-only bar when it would
   * otherwise hide itself entirely (for non signed-in users using a branded
   * layout). only the asset-name title renders -- no publication state,
   * edit button nor right side 3-dot kebab menu actions. combined with
   * `titleAsHeading`, this lets the DSLP skip link always land on the `<h1>`,
   * even for non signed-in users.
   */
  alwaysRenderTitle?: boolean;

  // Filter where clauses from grid tables for report scheduling - componentUuid -> filterWhereClause
  filterWhereClauses?: Record<string, string | null>;
  // Filter metadata from grid tables for report scheduling - componentUuid -> filter metadata
  filterMetadata?: FilterMetadataCollection;
}

interface AssetActionBarState {
  userCanCreateMoreAssets: boolean;
  approvalsGuidance?: GuidanceSummaryV2;
}

class AssetActionBar extends Component<PropsWithChildren<AssetActionBarProps>, AssetActionBarState> {
  willNotShowBarBecauseUserCantDoAnything: () => void;
  willShowBar: () => void;
  elRef: RefObject<HTMLDivElement>;

  constructor(props: PropsWithChildren<AssetActionBarProps>) {
    super(props);

    const { view } = this.props;

    if (!view) {
      throw new Error('The Asset Action Bar does not make much sense without a current view.');
    }

    if (isStory(view) && !window.STORY_DATA) {
      airbrake.notify({
        error: 'Using Asset Action Bar on a Story requires a window object.',
        context: { component: 'AssetActionBar' }, // provide useful information here about where the error occurred
      });
    }

    this.elRef = React.createRef();
    this.willNotShowBarBecauseUserCantDoAnything = _.once(
      props.willNotShowBarBecauseUserCantDoAnything || _.noop,
    );
    this.willShowBar = _.once(props.willShowBar || _.noop);

    this.state = {
      userCanCreateMoreAssets: false,
    };
  }

  onWindowResize = () => {
    this.forceUpdate();
  };

  fetchUserCanMakeAssets = async () => {
    const userCanCreateMoreAssets = await currentUserCanMakeAssets();
    this.setState({ userCanCreateMoreAssets });
  };

  componentDidMount() {
    window.addEventListener('resize', this.onWindowResize);

    if (!this.props.approvalsGuidance) {
      fetchApprovalsGuidanceV2(
        assetIdFor(this.props.view.id, getRevisionSeq(), isStoryDraft(this.props.view)),
      ).then((approvalsGuidance: GuidanceSummaryV2) => {
        this.setState({ approvalsGuidance });
      });
    }

    this.fetchUserCanMakeAssets();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  renderFederationTargets() {
    const { view } = this.props;

    // don't render federation targets if we're on a draft
    if (assetIsDraft({ coreView: view })) {
      return null;
    }

    const hasFederationTargets = view.federatedTo && view.federatedTo.length > 0;
    if (hasFederationTargets) {
      return [
        <div className="divider" key="federation-targets-divider" />,
        <FederationTargets view={view} key="federation-targets" />,
      ];
    } else {
      return [];
    }
  }

  renderTitleHeading() {
    const { view, titleOverride, titleAsHeading } = this.props;
    if (titleOverride) {
      return titleOverride;
    }

    const titleClass = 'asset-name forge-typography--subheading4';
    const titleText = _.get(view, 'name');
    const titleTextOrDefault = titleText || I18n.t('default_asset_title', { scope });
    // fall back to a generic title only for the DSLP <h1>, so the skip-link
    // always lands on a heading with an accessible name even if the asset is unnamed
    return titleAsHeading ? (
      <h1 id="dslp-main-heading" tabIndex={-1} className={titleClass} title={titleTextOrDefault}>
        {titleTextOrDefault}
      </h1>
    ) : (
      <span className={titleClass} title={titleText}>
        {titleText}
      </span>
    );
  }

  /**
   * renders the AssetActionBar with only the asset-name title -- no
   * publication state, edit button nor right side 3-dot kebab menu actions.
   * used when `alwaysRenderTitle` is set and the AAB would otherwise hide
   * itself entirely, so the title stays in the DOM. combined with `titleAsHeading`,
   * the title renders as the `<h1>` skip-link / landmark target.
   * see the `alwaysRenderTitle` prop.
   */
  renderTitleOnly() {
    return (
      <div className="asset-action-bar" ref={this.elRef}>
        <div className="left-side">
          <div className="asset-action-bar-item">{this.renderTitleHeading()}</div>
        </div>
      </div>
    );
  }

  renderLeftSideInformation(approvalsGuidance: GuidanceSummaryV2) {
    const { leftChildren, view } = this.props;

    return (
      <div className={classNames({ 'left-side': true, mobile: isMobileOrTablet() })}>
        {leftChildren}
        <div className="asset-action-bar-item">{this.renderTitleHeading()}</div>
        <div className="divider" />
        <PublicationState view={view} approvalsGuidance={approvalsGuidance} />
        {this.renderFederationTargets()}
      </div>
    );
  }

  renderRightSideActions(approvalsGuidance: GuidanceSummaryV2) {
    const {
      children,
      editDraftButton,
      onCopy,
      copyBasedOn,
      onPublish,
      renderPrimaryButton,
      showDSLPButton,
      view,
      customSaveFunc,
      inVQE,
      personalizedStory,
      filterWhereClauses,
      filterMetadata,
    } = this.props;

    const { userCanCreateMoreAssets } = this.state;
    const publishedViewUid = this.props.publishedViewUid || getPublishedViewUid(view, approvalsGuidance);
    const assetStatus = assetStatusFor(view, approvalsGuidance);

    /*
     * The AssetActionBar component is divided up into multiple logical
     * UI groups, each group consisting of UI components that may require
     * the user to have a certain set of permissions or the asset to be in
     * a particular state (published vs draft, etc).
     *
     * In order to hide UI that the user can't make use of, we have a
     * concept of "allowed actions" that take into account both rights
     * and asset state, as well as business logic (such as preferring
     * a publication button over "edit draft", even though both are
     * allowable).
     *
     * If AssetActionBar is displayed in VQE (aka Exploration Canvas), AAB will
     * only hide itself entirely if there are *no* actions allowed and no child
     * components, including an asset title. A special VQE-only `Create View`
     * button is passed as a child to AAB and will be rendered for any logged-in user.
     */
    const allowedActions = getAllowedSecondaryActions({
      approvalsGuidance,
      assetStatus,
      view,
      publishedViewUid,
      userCanCreateMoreAssets,
      showDSLPButton,
    });

    const primaryAction = getPrimaryAction({
      appProvidesOwnPrimaryButton: !!renderPrimaryButton,
      assetStatus,
      approvalsGuidance,
      view,
      showDSLPButton,
    });

    if (!anyActionAvailable(primaryAction, allowedActions) && !(inVQE && children)) {
      return null;
    }

    const rightSideParts: ReactNode[] = [];

    if (allowedActions.viewOnSourceDomain) {
      rightSideParts.push(<ViewOnSourceDomainButton key="view-on-source-domain-button" view={view} />);
    }

    if (allowedActions.save) {
      // looking at an unpublished grid view dataset that we have the "write" right for,
      // we show the "Save" button
      rightSideParts.push(<SaveViewButton key="save-view-button" />);
    }

    if (
      FeatureFlags.valueOrDefault('enable_saved_state_in_stories', false) &&
      isStory(view) &&
      personalizedStory
    ) {
      rightSideParts.push(<ResetToDefaultButton key="reset-to-default-button" />);
    }

    rightSideParts.push(
      <PublicationAction
        key="publication-action"
        primaryAction={primaryAction}
        allowedActions={allowedActions}
        approvalsGuidance={approvalsGuidance}
        publishedViewUid={publishedViewUid}
        view={view}
        editDraftButton={editDraftButton}
        renderPrimaryButton={renderPrimaryButton}
        onCopy={onCopy}
        copyBasedOn={copyBasedOn}
        onPublish={onPublish}
        customSaveFunc={customSaveFunc}
        filterWhereClauses={filterWhereClauses}
        {...(isTablePrintingImprovementForScheduledSendEnabled() && { filterMetadata })}
      />,
    );

    if (isMobileOrTablet()) {
      return (
        <div className="right-side-mobile">
          {children}
          {rightSideParts}
        </div>
      );
    }

    return (
      <div className="right-side">
        {children}
        {rightSideParts}
      </div>
    );
  }

  render() {
    const { user, alwaysRenderTitle } = this.props;
    const approvalsGuidance = this.state.approvalsGuidance || this.props.approvalsGuidance;

    // Non signed-in user cannot take any action via AAB, and assets
    // without guidance cannot be acted upon by AAB
    if (!user || !approvalsGuidance) {
      this.willNotShowBarBecauseUserCantDoAnything();
      return alwaysRenderTitle ? this.renderTitleOnly() : null;
    }

    const rightSideActions = this.renderRightSideActions(approvalsGuidance);
    if (!rightSideActions) {
      // Non signed-in user cannot take any actions, so hide the right side of AssetActionBar
      this.willNotShowBarBecauseUserCantDoAnything();
      return alwaysRenderTitle ? this.renderTitleOnly() : null;
    }

    this.willShowBar();

    return (
      <QueryClientProvider client={queryClient}>
        <div className="asset-action-bar" ref={this.elRef}>
          {this.renderLeftSideInformation(approvalsGuidance)}
          {rightSideActions}
        </div>
      </QueryClientProvider>
    );
  }
}

export default AssetActionBar;
